<template>
  <div>
    <div class="alert alert-info d-flex align-items-center mb-3">
      <i class="fa-solid fa-circle-info me-2"></i>
      "Psi ativo", nesta aba, é psicólogo com assinatura ativa.
    </div>

    <DashPeriodFilter
      :period="filters.period"
      :from="filters.from"
      :to="filters.to"
      :applied="appliedPeriod"
      :loading="loading"
      @change="onFilterChange"
    />

    <div v-if="loading && !loaded" class="text-center py-6 text-muted">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p class="mb-0">Carregando...</p>
    </div>

    <div v-else-if="errored" class="text-center py-6 text-muted">
      <i class="fa-solid fa-triangle-exclamation fa-2x mb-3"></i>
      <p class="mb-2">{{ errorMessage || "Não foi possível carregar os dados." }}</p>
      <button class="btn btn-sm btn-outline-primary" @click="fetch">Tentar novamente</button>
    </div>

    <div v-else>
      <!-- Indicadores novos -->
      <div class="row g-3 mb-3">
        <div class="col-6 col-xl-3" v-for="c in activeCards" :key="c.key">
          <div class="big-card h-100">
            <span class="big-icon"><i :class="c.icon"></i></span>
            <div class="big-info">
              <span class="big-value">{{ c.format(data[c.key]) }}</span>
              <span class="big-label">
                {{ c.label }}
                <i
                  v-if="c.tooltip"
                  class="fa-solid fa-circle-info ms-1"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  data-bs-custom-class="dash-tooltip-wide"
                  :title="c.tooltip"
                ></i>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cadastros/atividade (migrados de Usuários) -->
      <div class="row g-3 mb-3">
        <div class="col-6 col-xl-6" v-for="c in legacyCards" :key="c.key">
          <div class="big-card h-100">
            <span class="big-icon"><i :class="c.icon"></i></span>
            <div class="big-info">
              <span class="big-value">{{ numberBR(c.value) }}</span>
              <span class="big-label">
                {{ c.label }}
                <i
                  v-if="c.tooltip"
                  class="fa-solid fa-circle-info ms-1"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  data-bs-custom-class="dash-tooltip-wide"
                  :title="c.tooltip"
                ></i>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <!-- ABC curve -->
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <strong>Curva ABC de faturamento</strong>
              <i
                class="fa-solid fa-circle-info ms-1"
                data-bs-toggle="tooltip"
                data-bs-placement="bottom"
                data-bs-html="true"
                data-bs-custom-class="dash-tooltip-wide"
                :title="abcCurveTooltip"
              ></i>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-3">
                Classificação dos psicólogos conforme sua participação acumulada
                no faturamento bruto do período. Classe A representa os primeiros
                80% do faturamento, B os próximos 15% e C os 5% restantes.
              </p>
              <div class="row g-3 text-center">
                <div class="col-4" v-for="c in abcCurve" :key="c.class">
                  <div class="abc-tile">
                    <span class="abc-badge" :class="'abc-' + c.class.toLowerCase()">
                      {{ c.class }}
                    </span>
                    <div class="mt-2">
                      <strong class="fs-5">{{ numberBR(c.psychologists) }}</strong>
                      <div class="text-muted small">psicólogos</div>
                    </div>
                    <div class="mt-2">
                      <strong>{{ moneyBR(c.revenue) }}</strong>
                      <div class="text-muted small">{{ c.share }}% do faturamento</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-6">
          <WidgetCard title="Cadastros de psis (mensal)">
            <ChartBar :data="registrationTimeline" :options="stackedOptions" />
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-6">
          <WidgetCard title="Cadastros diários de psis">
            <ChartLine :data="dailyRegistrations" />
          </WidgetCard>
        </div>

        <div class="col-12 col-xl-4">
          <WidgetCard title="Tipos de psis assinantes">
            <span class="badge bg-light text-dark border acc-badge">total acumulado</span>
            <ChartDoughnut :data="subscriptionTypes" />
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-8">
          <WidgetCard title="Base ativa & Churn de psis assinantes">
            <ChartLine :data="baseAndChurn" />
          </WidgetCard>
        </div>

        <div class="col-12">
          <WidgetCard title="Sessões de psis: Psiapp+ vs Gratuito">
            <ChartBar :data="subscribersVsNon" :options="stackedOptions" />
          </WidgetCard>
        </div>

        <div class="col-12 col-xl-6">
          <WidgetCard title="Top psicólogos por faturamento" :fixed="false">
            <DashRankList :items="topByRevenueItems" />
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-6">
          <WidgetCard title="Top psicólogos por consultas" :fixed="false">
            <DashRankList :items="topPsysItems" />
          </WidgetCard>
        </div>

        <div class="col-12 col-xl-6">
          <DashLtvBlock
            title="LTV — Assinatura — somente custos com assinatura"
            count-label="Psicólogos"
            :count="data.ltv_subscription?.psychologists"
            :total="data.ltv_subscription?.total"
            :average="data.ltv_subscription?.average"
            :items="ltvItems(data.ltv_subscription)"
            note="Valor total pago pelo psicólogo em assinatura ao longo de todo o relacionamento com o Psiapp, menos reembolsos — vitalício, sem corte. O filtro só decide quem entra na lista."
          />
        </div>
        <div class="col-12 col-xl-6">
          <DashLtvBlock
            title="LTV — Receita total — assinaturas + sessões"
            count-label="Psicólogos"
            :count="data.ltv_revenue?.psychologists"
            :total="data.ltv_revenue?.total"
            :average="data.ltv_revenue?.average"
            :items="ltvItems(data.ltv_revenue)"
            note="Assinatura + comissão das consultas do psicólogo, ao longo de todo o relacionamento com o Psiapp — vitalício, sem corte. O filtro só decide quem entra na lista. Não é lucro — a comissão vem do extrato Stripe, com 97% de cobertura."
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  COLORS,
  moneyBR,
  numberBR,
  groupSeries,
  doughnut,
  baseOptions,
  dateTicks,
} from "@/utils/dashboardCharts";
import { Tooltip } from "bootstrap";
import WidgetCard from "./WidgetCard.vue";
import DashPeriodFilter from "./DashPeriodFilter.vue";
import DashRankList from "./DashRankList.vue";
import DashLtvBlock from "./DashLtvBlock.vue";

const typeLabel = (t) =>
  t === "user" ? "Pacientes" : t === "psychologist" ? "Psicólogos" : t;

export default {
  name: "DashPsychologists",
  components: { WidgetCard, DashPeriodFilter, DashRankList, DashLtvBlock },
  data() {
    return {
      loading: false,
      loaded: false,
      errored: false,
      errorMessage: "",
      filters: { period: "last_12_months", from: "", to: "" },
      appliedPeriod: null,
      data: {},
      stackedOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ stacked: true, ticks: dateTicks }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true } }],
        },
      },
      activeCards: [
        {
          key: "active",
          label: "Psis ativos no período",
          icon: "fa-solid fa-bolt",
          format: (v) => numberBR(v),
          tooltip: "Teve acesso em algum momento da janela. Cresce conforme a janela aumenta.",
        },
        {
          key: "active_at_end",
          label: "Psis ativos no fim do período",
          icon: "fa-solid fa-flag-checkered",
          format: (v) => numberBR(v),
          tooltip: "Ainda tinha acesso no último dia. É um retrato de hoje, não muda com o período.",
        },
        {
          key: "appointments_per_active",
          label: "Consultas por psi ativo",
          icon: "fa-solid fa-calendar-check",
          format: (v) => (Number(v) || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 }),
        },
        {
          key: "avg_ticket_per_active",
          label: "Faturamento médio por psi ativo",
          icon: "fa-solid fa-sack-dollar",
          format: (v) => moneyBR(v),
          tooltip:
            "Soma do faturamento bruto das sessões válidas no período, dividido pelos psis que tiveram assinatura ativa em algum momento dele — inclusive quem não atendeu ninguém. Mostra a produtividade da base de assinantes, não o ganho individual." +
            "<br><br>Não é preço médio da consulta (é volume × preço ÷ pessoas), nem o que o Psiapp fatura (é o valor cheio da sessão, ~80% vai pro psicólogo). Entre só quem de fato atendeu no período, a média fica bem mais alta.",
        },
      ],
    };
  },
  computed: {
    legacyCards() {
      return [
        {
          key: "new_psychologists",
          label: "Cadastros de psis no período",
          icon: "fa-solid fa-user-doctor",
          value: this.data.new_psychologists,
        },
        {
          key: "with_sessions",
          label: "Psis com sessão no período",
          icon: "fa-regular fa-clock",
          value: this.data.with_sessions?.psy,
          tooltip: "Psis com pelo menos uma sessão no período selecionado.",
        },
      ];
    },
    abcCurve() {
      return this.data.abc_curve || [];
    },
    abcCurveTooltip() {
      return [
        "Ordena os psis com sessão válida no período do maior faturamento bruto pro menor e vai somando: até 80% do acumulado é classe A, até 95% é B, o resto é C. A classe de um psi depende de quem está na frente dele, não do valor isolado — por isso o último do A e o primeiro do B costumam ser bem parecidos.",
        "Serve como régua de dependência: perder um psi do A costuma doer, perder vários do C não. Também mostra onde vale investir relacionamento (A), quem tem potencial de subir (B) e quem ainda está só experimentando (C).",
        "Ressalvas: é faturamento bruto (valor cheio da sessão), não a comissão do Psiapp — mas a concentração da comissão é parecida, já que a taxa é proporcional. Só entra quem teve sessão válida no período (psis ativos sem atendimento ficam de fora). Em períodos curtos a curva fica mais concentrada, porque menos gente atende.",
      ].join("<br><br>");
    },
    registrationTimeline() {
      const rows = (this.data.registration_timeline || []).map((r) => ({
        ...r,
        type: typeLabel(r.type),
      }));
      return groupSeries(rows, "year_month", "type", "total");
    },
    dailyRegistrations() {
      const rows = (this.data.daily_registrations || []).map((r) => ({
        ...r,
        type: typeLabel(r.type),
      }));
      return groupSeries(rows, "day", "type", "total");
    },
    subscriptionTypes() {
      return doughnut(this.data.subscription_types || [], "plano", "assinantes");
    },
    baseAndChurn() {
      const rows = this.data.base_and_churn || [];
      const labels = rows.map((r) => r.year_month);
      const series = [
        { key: "active_base", label: "Base ativa" },
        { key: "new_subscriptions", label: "Novas" },
        { key: "cancellations", label: "Cancelamentos" },
        { key: "net_growth", label: "Crescimento líquido" },
        { key: "churn_percent", label: "Churn %" },
      ];
      return {
        labels,
        datasets: series.map((s, i) => ({
          label: s.label,
          data: rows.map((r) => Number(r[s.key])),
          borderColor: COLORS[i % COLORS.length],
          backgroundColor: COLORS[i % COLORS.length],
          fill: false,
        })),
      };
    },
    subscribersVsNon() {
      return groupSeries(this.data.subscribers_vs_non || [], "year_month", "subscription_status", "total");
    },
    topByRevenueItems() {
      return this.withPerc(this.data.top_by_revenue, "revenue").map((r) => ({
        id: r.id,
        name: r.name,
        subtitle: `${numberBR(r.appointments)} consultas`,
        value: moneyBR(r.revenue),
        perc: r.perc,
        linkTo: `${this.$paths.psychologists.detail}/${r.id}`,
      }));
    },
    topPsysItems() {
      return this.withPerc(this.data.top_psys, "appointment_count").map((r) => ({
        id: r.id,
        name: r.name,
        subtitle: r.email,
        value: numberBR(r.appointment_count),
        unit: "consultas",
        perc: r.perc,
        linkTo: `${this.$paths.psychologists.detail}/${r.id}`,
      }));
    },
  },
  mounted() {
    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']",
    });
    this.fetch();
  },
  methods: {
    moneyBR,
    numberBR,
    onFilterChange(f) {
      this.filters = f;
      this.fetch();
    },
    async fetch() {
      this.loading = true;
      this.errored = false;
      try {
        const res = await this.$dashboardService.psychologists(this.filters);
        this.data = res.data || {};
        this.appliedPeriod = res.period || null;
        this.loaded = true;
      } catch (e) {
        this.errored = true;
        this.errorMessage = e.message;
      } finally {
        this.loading = false;
      }
    },
    withPerc(list, key) {
      const rows = list || [];
      const max = rows.reduce((m, r) => Math.max(m, Number(r[key]) || 0), 0);
      return rows.map((r) => ({
        ...r,
        perc: max ? Math.round(((Number(r[key]) || 0) / max) * 100) : 0,
      }));
    },
    ltvItems(block) {
      return this.withPerc(block?.top, "ltv").map((r) => ({
        id: r.id,
        name: r.name,
        value: moneyBR(r.ltv),
        perc: r.perc,
        linkTo: `${this.$paths.psychologists.detail}/${r.id}`,
      }));
    },
  },
};
</script>

<style scoped>
.big-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  background-color: #fff;
  border: 1px solid #edf0f5;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(18, 38, 63, 0.04);
}
.big-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex: 0 0 46px;
  background-color: rgba(251, 164, 134, 0.16);
  color: var(--bs-primary, #fba486);
}
.big-info {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  min-width: 0;
}
.big-value {
  font-size: 1.7rem;
  font-weight: 800;
  color: #12263f;
  letter-spacing: -0.5px;
}
.big-label {
  font-size: 0.78rem;
  color: #8896a6;
  margin-top: 2px;
}
.abc-tile {
  padding: 12px;
}
.abc-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-weight: 800;
  font-size: 1.1rem;
}
.abc-a {
  background-color: #d7f5e9;
  color: #0f7a53;
}
.abc-b {
  background-color: #fdf3d8;
  color: #a67c00;
}
.abc-c {
  background-color: #fde8ec;
  color: #b3384f;
}
.acc-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 0.65rem;
}
</style>
